import { LinearProgress, Slider, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import { BulkCleanBinsProgressState } from "~/features/preview/bins/binSettings.types";
import { translationKeyForCount } from "~/features/preview/bins/binSettings.utils";

type Props = {
  progress: BulkCleanBinsProgressState;
};

export function BulkCleanBinsProgress({ progress }: Props) {
  const { t } = useTranslation();
  const { totalBins, remainingBins, totalFlags, remainingFlags, currentBinId } =
    progress;

  const completedBins = totalBins - remainingBins;
  const percentComplete =
    totalBins > 0 ? Math.round((completedBins / totalBins) * 100) : 0;
  const binLabel = t(
    translationKeyForCount("bin", "bins", remainingBins)
  ).toLowerCase();

  return (
    <Stack spacing={2.5} sx={{ py: 2 }}>
      <Typography variant="body1" fontWeight={600}>
        {t("bulk clean bins progress title")}
      </Typography>

      <Stack spacing={1}>
        <Typography variant="h5" component="p" fontWeight={600}>
          {remainingBins.toLocaleString()} {binLabel}{" "}
          {t("bulk clean bins progress remaining")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {remainingFlags.toLocaleString()} {t("bulk clean bins progress of")}{" "}
          {totalFlags.toLocaleString()}{" "}
          {t("bulk clean bins progress flags remaining")}
        </Typography>
        {currentBinId != null ? (
          <Typography variant="caption" color="text.secondary">
            {t("bulk clean bins progress processing bin")} {currentBinId}
          </Typography>
        ) : null}
      </Stack>

      <Slider
        value={remainingBins}
        min={0}
        max={totalBins}
        disabled
        aria-label={t("bulk clean bins progress slider label")}
        sx={{ px: 0.5 }}
      />

      <Stack spacing={0.5}>
        <LinearProgress variant="determinate" value={percentComplete} />
        <Typography variant="caption" color="text.secondary">
          {percentComplete}% {t("bulk clean bins progress complete")}
        </Typography>
      </Stack>
    </Stack>
  );
}
